import { useEffect } from 'react'
import { animated, config, useSpring, useTransition } from '@react-spring/web'
import PropTypes from 'prop-types'

import Box from '@mui/material/Box'
import { alpha } from '@mui/material/styles'

import { useReducedMotion } from '@utils/AnimationsHelper'

AnimatedIcon.propTypes = {
  /** Icon component to display (MUI icon component) */
  icon: PropTypes.elementType.isRequired,

  /** Diameter of the tinted disc, in px */
  size: PropTypes.number,
}

// Settles with a slight overshoot, ~6%, rather than bouncing
const MORPH_CONFIG = { mass: 1, tension: 300, friction: 23 }

export default function AnimatedIcon({ icon: Icon, size = 120 }) {
  const reducedMotion = useReducedMotion()

  const [floatingStyle, floatingApi] = useSpring(() => ({
    from: { y: 0, rotate: 0 },
    config: config.slow,
  }))

  const dot1Spring = useSpring({
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    delay: 700,
    config: config.wobbly,
  })

  const dot2Spring = useSpring({
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    delay: 800,
    config: config.wobbly,
  })

  const dot3Spring = useSpring({
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    delay: 900,
    config: config.wobbly,
  })

  const transitions = useTransition(Icon, {
    // By component identity, not `muiName` — every MUI icon reports 'SvgIcon', so keying on
    // that would collide them all into one and no morph would ever fire.
    keys: (icon) => icon,
    // `initial`, not `from`, so an icon that never changes does not animate on mount
    initial: { opacity: 1, scale: 1, rotate: 0 },
    from: { opacity: 0, scale: 0.6, rotate: -20 },
    enter: { opacity: 1, scale: 1, rotate: 0 },
    leave: { opacity: 0, scale: 0.6, rotate: 20 },
    config: reducedMotion ? { duration: 0 } : MORPH_CONFIG,
    immediate: reducedMotion,
  })

  useEffect(() => {
    floatingApi.start({
      to: async (next) => {
        while (true) {
          await next({ y: 8, rotate: 4 })
          await next({ y: 0, rotate: 0 })
        }
      },
      config: { duration: 3000 },
    })

    return () => floatingApi.stop()
  }, [floatingApi])

  return (
    <Box
      component={animated.div}
      sx={styles.container}
      style={{
        transform: floatingStyle.y.to((y) => `translateY(${y}px) rotate(${floatingStyle.rotate.get()}deg)`),
      }}
    >
      <Box sx={styles.iconBackground(size)}>
        {transitions((style, MorphIcon) => (
          <Box
            component={animated.div}
            sx={styles.iconLayer}
            style={{
              opacity: style.opacity,
              transform: style.scale.to((s) => `scale(${s}) rotate(${style.rotate.get()}deg)`),
            }}
          >
            <MorphIcon sx={styles.icon(size)} />
          </Box>
        ))}

        <Box component={animated.div} sx={[styles.dot, styles.dot1]} style={dot1Spring} />
        <Box component={animated.div} sx={[styles.dot, styles.dot2]} style={dot2Spring} />
        <Box component={animated.div} sx={[styles.dot, styles.dot3]} style={dot3Spring} />
      </Box>
    </Box>
  )
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  iconBackground: (size) => ({
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.15),
    boxShadow: (theme) => theme.shadows[8],
  }),
  icon: (size) => ({
    fontSize: Math.round(size * 0.53),
    color: 'primary.main',
  }),
  iconLayer: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dot: {
    position: 'absolute',
    borderRadius: '50%',
  },
  dot1: {
    backgroundColor: 'warning.main',
    width: 12,
    height: 12,
    top: 15,
    right: 10,
  },
  dot2: {
    backgroundColor: 'success.main',
    width: 8,
    height: 8,
    bottom: 20,
    left: 15,
  },
  dot3: {
    backgroundColor: 'secondary.main',
    width: 10,
    height: 10,
    top: 30,
    left: 5,
  },
}
